import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  Collapse,
  Divider,
  Switch,
  Tooltip,
  TextField,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Delete,
  Edit,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Save,
  Close,
} from '@mui/icons-material';
import styles from './TransactionCard.module.css';

interface Transaction {
  tscId: number;
  tscName: string;
  tccId: number;
  tccName: string;
  tscValue: number;
  tctId: 1 | 2;
  tctName: "Saída" | "Entrada";
  tscMonth: number;
  tscYear: number;
  tscRecurrence: number;
  tscRecurrenceDate: string;
  tscDateCreated: string;
  tscVersion: number;
  tscIdInitVersion: number;
  usrId: number;
  usrIdTransaction: number;
  usrName: string;
  tscCheck: number;
}

interface TransactionCardProps {
  transaction: Transaction;
  isMonthClosed: boolean;
  onDelete: (id: number) => void;
  onUpdateCheck: (id: number, checked: number) => void;
  onUpdateValue: (id: number, value: number) => void;
  onDeleteConfirm: (event: React.MouseEvent<HTMLButtonElement>, id: number) => void;
  onCheckConfirm: (event: React.MouseEvent<HTMLElement>, id: number, checked: number) => void;
}

export function TransactionCard({
  transaction,
  isMonthClosed,
  onDelete,
  onUpdateCheck,
  onUpdateValue,
  onDeleteConfirm,
  onCheckConfirm,
}: TransactionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(transaction.tscValue.toString());

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditValue(transaction.tscValue.toString());
  };

  const handleSaveEdit = () => {
    const numericValue = parseFloat(editValue.replace(/[^\d,.-]/g, '').replace(',', '.'));
    if (!isNaN(numericValue) && numericValue > 0) {
      onUpdateValue(transaction.tscId, numericValue);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(transaction.tscValue.toString());
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const isEntrada = transaction.tctName === "Entrada";
  const isPago = transaction.tscCheck === 1;

  return (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 2, 
        boxShadow: 1,
        borderLeft: `4px solid ${isEntrada ? '#4caf50' : '#f44336'}`,
        minHeight: 120,
        width: '100%',
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'white',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-1px)',
        },
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 2, minHeight: 80 }}>
        {/* Header do Card - Sempre Visível */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 1 }}>
              {transaction.tscName}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <TextField
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    size="small"
                    variant="outlined"
                    placeholder="0,00"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: isEntrada ? '#4caf50' : '#f44336'
                      }
                    }}
                  />
                  <IconButton size="small" onClick={handleSaveEdit} sx={{ color: '#4caf50' }}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEdit} sx={{ color: '#f44336' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: '1.25rem',
                    color: isEntrada ? '#4caf50' : '#f44336'
                  }}
                >
                  {formatCurrency(transaction.tscValue)}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={isEntrada ? <TrendingUp /> : <TrendingDown />}
                  label={transaction.tctName}
                  size="small"
                  sx={{
                    backgroundColor: isEntrada ? '#e8f5e8' : '#ffebee',
                    color: isEntrada ? '#2e7d32' : '#c62828',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
                
                {transaction.tctName === "Saída" && (
                  <Chip
                    icon={isPago ? <CheckCircle /> : <Cancel />}
                    label={isPago ? "Pago" : "Pendente"}
                    size="small"
                    sx={{
                      backgroundColor: isPago ? '#e8f5e8' : '#fff3e0',
                      color: isPago ? '#2e7d32' : '#ef6c00',
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}
                  />
                )}
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {transaction.tccName}
            </Typography>
          </Box>
          
          <IconButton
            onClick={handleExpandClick}
            size="small"
            sx={{ color: 'text.secondary', p: 0.5, ml: 1 }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Detalhes Expansíveis */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: 2, 
              mb: 2 
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Usuário
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {transaction.usrName}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Data de Criação
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {formatDate(transaction.tscDateCreated)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Recorrência
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {transaction.tscRecurrence === 1 ? 'Sim' : 'Não'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Período
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {transaction.tscMonth}/{transaction.tscYear}
                </Typography>
              </Box>
            </Box>

            {/* Ações */}
            {!isMonthClosed && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  {transaction.tctName === "Saída" && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Pago
                      </Typography>
                      <Switch
                        checked={isPago}
                        onChange={(e) => onCheckConfirm(e as any, transaction.tscId, isPago ? 0 : 1)}
                        size="small"
                        color="success"
                      />
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar Valor">
                      <IconButton
                        size="small"
                        sx={{
                          color: '#1976d2',
                          backgroundColor: '#e3f2fd',
                          borderRadius: 1,
                          p: 0.75,
                          '&:hover': {
                            backgroundColor: '#1976d2',
                            color: 'white'
                          }
                        }}
                        onClick={handleEditClick}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        sx={{
                          color: '#d32f2f',
                          backgroundColor: '#ffebee',
                          borderRadius: 1,
                          p: 0.75,
                          '&:hover': {
                            backgroundColor: '#d32f2f',
                            color: 'white'
                          }
                        }}
                        onClick={(e) => onDeleteConfirm(e, transaction.tscId)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}